/**
 * GraphQL Caching Utility
 * Implements intelligent caching with cache invalidation for GraphQL queries
 * Handles data updates and maintains cache consistency
 */

class GraphQLCache {
  constructor() {
    this.cache = new Map();
    this.subscribers = new Map();
    this.ttl = new Map(); // Time-to-live for cache entries
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default TTL
    this.maxCacheSize = 100; // Maximum number of cached queries
    this.cacheHits = 0;
    this.cacheMisses = 0;
  }

  /**
   * Generate cache key from query and variables
   * @param {string} query - GraphQL query string
   * @param {object} variables - Query variables
   * @returns {string} Cache key
   */
  generateKey(query, variables = {}) {
    // Extract query name for better debugging
    const queryName = this.extractQueryName(query);
    const variablesKey = JSON.stringify(variables);
    return `${queryName}:${this.hashString(variablesKey)}`;
  }

  /**
   * Extract query name from GraphQL query string
   * @param {string} query - GraphQL query
   * @returns {string} Query name
   */
  extractQueryName(query) {
    const match = query.match(/query\s+(\w+)/);
    return match ? match[1] : 'unknown';
  }

  /**
   * Simple hash function for cache keys
   * @param {string} str - String to hash
   * @returns {string} Hash
   */
  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
  }

  /**
   * Get cached data if available and not expired
   * @param {string} query - GraphQL query
   * @param {object} variables - Query variables
   * @returns {object|null} Cached data or null
   */
  get(query, variables = {}) {
    const key = this.generateKey(query, variables);
    const cached = this.cache.get(key);
    
    if (!cached) {
      this.cacheMisses++;
      return null;
    }

    // Check if cache entry has expired
    const now = Date.now();
    const expiry = this.ttl.get(key);
    
    if (expiry && now > expiry) {
      this.cache.delete(key);
      this.ttl.delete(key);
      this.cacheMisses++;
      return null;
    }

    this.cacheHits++;
    console.log(`[GraphQL Cache] Cache HIT for query: ${this.extractQueryName(query)}`);
    return cached;
  }

  /**
   * Store data in cache with TTL
   * @param {string} query - GraphQL query
   * @param {object} variables - Query variables
   * @param {object} data - Data to cache
   * @param {number} customTTL - Custom TTL in milliseconds
   */
  set(query, variables = {}, data, customTTL = null) {
    const key = this.generateKey(query, variables);
    
    // Implement LRU eviction if cache is full
    if (this.cache.size >= this.maxCacheSize && !this.cache.has(key)) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
      this.ttl.delete(firstKey);
    }

    this.cache.set(key, data);
    
    // Set TTL
    const ttl = customTTL || this.getTTLForQuery(query);
    if (ttl > 0) {
      this.ttl.set(key, Date.now() + ttl);
    }

    console.log(`[GraphQL Cache] Cached query: ${this.extractQueryName(query)} (TTL: ${ttl}ms)`);
  }

  /**
   * Get appropriate TTL for different query types
   * @param {string} query - GraphQL query
   * @returns {number} TTL in milliseconds
   */
  getTTLForQuery(query) {
    const queryName = this.extractQueryName(query);
    
    // Different TTL based on query type
    if (queryName.includes('list') || queryName.includes('search')) {
      return 2 * 60 * 1000; // 2 minutes for list queries
    }
    
    if (queryName.includes('get') && queryName.includes('Product')) {
      return 5 * 60 * 1000; // 5 minutes for individual products
    }
    
    if (queryName.includes('featured') || queryName.includes('category')) {
      return 10 * 60 * 1000; // 10 minutes for relatively stable data
    }
    
    return this.defaultTTL;
  }

  /**
   * Invalidate cache entries based on patterns
   * @param {string|Array} patterns - Patterns to match for invalidation
   */
  invalidate(patterns) {
    const patternsArray = Array.isArray(patterns) ? patterns : [patterns];
    let invalidatedCount = 0;

    for (const [key, data] of this.cache.entries()) {
      const shouldInvalidate = patternsArray.some(pattern => {
        if (typeof pattern === 'string') {
          return key.includes(pattern);
        }
        if (pattern instanceof RegExp) {
          return pattern.test(key);
        }
        return false;
      });

      if (shouldInvalidate) {
        this.cache.delete(key);
        this.ttl.delete(key);
        invalidatedCount++;
        
        // Notify subscribers
        this.notifySubscribers(key, 'invalidated');
      }
    }

    console.log(`[GraphQL Cache] Invalidated ${invalidatedCount} cache entries`);
  }

  /**
   * Invalidate product-related cache entries
   * @param {string} productId - Product ID that was updated
   */
  invalidateProduct(productId) {
    const patterns = [
      'listProducts',
      'listProductsWithImages',
      'listProductsWithPrimaryImages',
      'searchProducts',
      'productsByCategory',
      'listFeaturedProducts',
      productId ? `getProduct:${productId}` : null
    ].filter(Boolean);

    this.invalidate(patterns);
  }

  /**
   * Invalidate category-related cache entries
   * @param {string} categoryId - Category ID that was updated
   */
  invalidateCategory(categoryId) {
    const patterns = [
      'listCategories',
      'productsByCategory',
      categoryId ? `getCategory:${categoryId}` : null
    ].filter(Boolean);

    this.invalidate(patterns);
  }

  /**
   * Subscribe to cache changes for reactive updates
   * @param {string} pattern - Pattern to watch
   * @param {function} callback - Callback for changes
   */
  subscribe(pattern, callback) {
    if (!this.subscribers.has(pattern)) {
      this.subscribers.set(pattern, new Set());
    }
    this.subscribers.get(pattern).add(callback);
  }

  /**
   * Unsubscribe from cache changes
   * @param {string} pattern - Pattern to stop watching
   * @param {function} callback - Callback to remove
   */
  unsubscribe(pattern, callback) {
    const patternSubscribers = this.subscribers.get(pattern);
    if (patternSubscribers) {
      patternSubscribers.delete(callback);
    }
  }

  /**
   * Notify subscribers about cache changes
   * @param {string} key - Cache key that changed
   * @param {string} action - Action type (invalidated, updated)
   */
  notifySubscribers(key, action) {
    for (const [pattern, callbacks] of this.subscribers.entries()) {
      if (key.includes(pattern)) {
        callbacks.forEach(callback => {
          try {
            callback({ key, action, timestamp: Date.now() });
          } catch (error) {
            console.error('[GraphQL Cache] Subscriber callback error:', error);
          }
        });
      }
    }
  }

  /**
   * Clear all cache entries
   */
  clear() {
    const size = this.cache.size;
    this.cache.clear();
    this.ttl.clear();
    console.log(`[GraphQL Cache] Cleared ${size} cache entries`);
  }

  /**
   * Get cache statistics
   * @returns {object} Cache stats
   */
  getStats() {
    const totalRequests = this.cacheHits + this.cacheMisses;
    const hitRate = totalRequests > 0 ? (this.cacheHits / totalRequests * 100).toFixed(2) : 0;
    
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      hits: this.cacheHits,
      misses: this.cacheMisses,
      hitRate: `${hitRate}%`,
      totalRequests
    };
  }

  /**
   * Preload cache with common queries
   * @param {function} graphqlClient - GraphQL client function
   */
  async preload(graphqlClient) {
    const commonQueries = [
      { query: 'listCategories', variables: { filter: { isActive: { eq: true } } } },
      { query: 'listFeaturedProducts', variables: { filter: { isActive: { eq: true } } } }
    ];

    console.log('[GraphQL Cache] Preloading common queries...');
    
    for (const { query, variables } of commonQueries) {
      try {
        await this.executeWithCache(graphqlClient, query, variables);
      } catch (error) {
        console.warn(`[GraphQL Cache] Failed to preload ${query}:`, error);
      }
    }
  }

  /**
   * Execute GraphQL query with caching
   * @param {function} graphqlClient - GraphQL client function
   * @param {string} query - GraphQL query
   * @param {object} variables - Query variables
   * @returns {Promise<object>} Query result
   */
  async executeWithCache(graphqlClient, query, variables = {}) {
    // Check cache first
    const cached = this.get(query, variables);
    if (cached) {
      return cached;
    }

    // Execute query
    try {
      // graphqlClient is now a function that already has all params bound
      const result = await graphqlClient();

      // Cache successful results
      if (result && !result.errors) {
        this.set(query, variables, result);
      }

      return result;
    } catch (error) {
      console.error('[GraphQL Cache] Query execution failed:', error);
      throw error;
    }
  }
}

// Create singleton instance
const graphqlCache = new GraphQLCache();

// Export cache instance and helper functions
export default graphqlCache;

export const getCachedQuery = (query, variables) => graphqlCache.get(query, variables);
export const setCachedQuery = (query, variables, data, ttl) => graphqlCache.set(query, variables, data, ttl);
export const invalidateCache = (patterns) => graphqlCache.invalidate(patterns);
export const invalidateProductCache = (productId) => graphqlCache.invalidateProduct(productId);
export const invalidateCategoryCache = (categoryId) => graphqlCache.invalidateCategory(categoryId);
export const clearCache = () => graphqlCache.clear();
export const getCacheStats = () => graphqlCache.getStats();

/**
 * Higher-order function to wrap GraphQL client with caching
 * @param {function} originalClient - Original GraphQL client
 * @returns {function} Wrapped client with caching
 */
export function withCache(originalClient) {
  return async (queryObject) => {
    // Pass through all parameters including authMode
    const { query, variables, ...otherParams } = queryObject;

    // For cached queries, we need to include authMode in the cache execution
    const executeQuery = async () => {
      return originalClient({ query, variables, ...otherParams });
    };

    return graphqlCache.executeWithCache(executeQuery, query, variables);
  };
}

/**
 * React hook for cache statistics (optional)
 * @returns {object} Cache statistics
 */
export function useCacheStats() {
  // This would typically use React hooks in a real implementation
  return graphqlCache.getStats();
}